import { createSelector, createSlice } from '@reduxjs/toolkit';
import { attributeCompareModels } from './attribution-compare-models';
import { clearSites, selectSite } from 'features/topbar/selectedSiteSlice';
import { RootState } from 'app/store';
import { PURGE } from 'redux-persist';
import { AttributionModelTypes } from 'common/constants/attribution-model-types';
import { AttributionCompareModel } from './attribution-compare-model.types';
import { resetAdvancedReports } from 'features/advanced-reports/advanced-reports.middleware';
import { togglePoc } from 'features/poc/poc-toggle/pocToggleSlice';

const initialState = {
  availableAttributionCompareModels: attributeCompareModels,
  activeAttributionCompareModel: AttributionModelTypes.FirstClick,
  isAttributionComparisonActive: false
};

const defaultAttributionCompareModel = {
  id: AttributionModelTypes.FirstClick,
  modelName: 'First Click'
} as AttributionCompareModel;

const availableAttributionCompareModelsSlice = createSlice({
  name: 'availableAttributionCompareModels',
  initialState,
  reducers: {
    selectAttributionCompareModel: (state, { payload }) => {
      state.activeAttributionCompareModel = payload;
    },
    setDisplayAttributionComparisonData: (state, { payload }) => {
      state.isAttributionComparisonActive = payload;
    },
    enableImpressionsAttributionModelDefault: (state) => {
      if (
        state.availableAttributionCompareModels.find(
          (model) => model.modelName === AttributionModelTypes.Impressions
        )
      ) {
        return;
      }

      state.availableAttributionCompareModels = [
        ...state.availableAttributionCompareModels,
        {
          modelName: 'Impressions',
          id: AttributionModelTypes.Impressions,
          isPocOnly: true
        }
      ];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(clearSites, (state) => {
      state.availableAttributionCompareModels = attributeCompareModels;
    });
    builder.addCase(selectSite, (state) => {
      state.availableAttributionCompareModels = attributeCompareModels;
    });
    builder.addCase(PURGE, () => initialState);
    builder.addCase(resetAdvancedReports, () => initialState);
    builder.addCase(togglePoc, (state) => {
      state.availableAttributionCompareModels =
        state.availableAttributionCompareModels.filter(
          (attributeModelRoute) =>
            attributeModelRoute.id !== AttributionModelTypes.DataDriven
        );

      if (
        !state.availableAttributionCompareModels.find(
          (attributeModelRoute) =>
            attributeModelRoute.id === AttributionModelTypes.Impressions
        )
      ) {
        enableImpressionsAttributionModelDefault();
      }
    });
  }
});

const selectSelf = (state: RootState) =>
  state.availableAttributionCompareModels;

const selectAttributionModelsWithPocState = (
  state: RootState,
  pocEnabled: boolean
) => {
  return state.availableAttributionCompareModels.availableAttributionCompareModels.filter(
    (model) => !model.isPocOnly || pocEnabled
  );
};

export const getAvailableAttributionCompareModels = createSelector(
  selectAttributionModelsWithPocState,
  (availableAttributionCompareModels) => availableAttributionCompareModels
);

export const selectIsAttributionComparisonActive = createSelector(
  selectSelf,
  (state) => {
    return state.isAttributionComparisonActive;
  }
);

export const getSelectedAttributionCompareModel = createSelector(
  selectSelf,
  selectAttributionModelsWithPocState,
  (state, availableAttributionCompareModels) =>
    availableAttributionCompareModels.find(
      (model) => model.id === state.activeAttributionCompareModel
    ) ?? defaultAttributionCompareModel
);

export const getSelectedAttributionCompareModelString = createSelector(
  selectSelf,
  selectAttributionModelsWithPocState,
  (state, availableAttributionCompareModels) =>
    availableAttributionCompareModels
      .find((model) => model.id === state.activeAttributionCompareModel)
      ?.id.toString() ?? defaultAttributionCompareModel.id.toString()
);

export const { actions, reducer } = availableAttributionCompareModelsSlice;

export const {
  selectAttributionCompareModel,
  setDisplayAttributionComparisonData,
  enableImpressionsAttributionModelDefault
} = actions;

export default reducer;
